import { useQuery, useMutation, useQueryClient } from 'react-query';
import api, { publicApi, UPLOAD_TIMEOUT } from '../utils/api';

/** Same filters as list; downloads CSV (requires edit access). */
export async function downloadCandidatesCsv(params = {}) {
  const res = await api.get('/candidates/export', { params, responseType: 'blob' });
  const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `candidates-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
}

// ── Cache helpers ───────────────────────────────────────────────────

const sameId = (row, id) => String(row?.id ?? row?._id ?? '') === String(id);

/** Fields that are request metadata, not candidate columns. */
const NON_FIELD_KEYS = ['stageNote', 'taskId', 'candidateId'];

function optimisticPatch(data) {
  const patch = {};
  for (const [key, value] of Object.entries(data)) {
    if (NON_FIELD_KEYS.includes(key) || value === undefined) continue;
    patch[key] = value;
  }
  return patch;
}

/** The first cached list row for this candidate, from any filter/page. */
function findCachedCandidate(qc, id) {
  if (!id) return null;
  for (const [, data] of qc.getQueriesData('candidates')) {
    const hit = data?.candidates?.find(c => sameId(c, id));
    if (hit) return hit;
  }
  for (const [, data] of qc.getQueriesData('pipeline')) {
    for (const cards of Object.values(data?.columns || {})) {
      const hit = cards.find(c => sameId(c, id));
      if (hit) return hit;
    }
  }
  return null;
}

function patchPipelineData(prev, id, patch) {
  if (!prev?.columns) return prev;
  let card = null;
  const columns = {};
  for (const [name, cards] of Object.entries(prev.columns)) {
    columns[name] = cards.filter(c => {
      if (!sameId(c, id)) return true;
      card = { ...c, ...patch };
      return false;
    });
  }
  if (!card) return prev;
  // A stage change moves the card to its new column; anything else goes back
  // where it was, so a star rating does not reshuffle the board.
  const target = patch.stage && columns[patch.stage] ? patch.stage : card.stage;
  if (columns[target]) columns[target] = [card, ...columns[target]];
  else columns[card.stage] = [card, ...(columns[card.stage] || [])];
  return { ...prev, columns };
}

function patchCandidateCaches(qc, id, patch) {
  if (!id || !Object.keys(patch).length) return;

  qc.setQueryData(['candidate', id], prev => (
    prev?.candidate ? { ...prev, candidate: { ...prev.candidate, ...patch } } : prev
  ));

  qc.setQueriesData('candidates', prev => {
    if (!Array.isArray(prev?.candidates)) return prev;
    let touched = false;
    const candidates = prev.candidates.map(c => {
      if (!sameId(c, id)) return c;
      touched = true;
      return { ...c, ...patch };
    });
    return touched ? { ...prev, candidates } : prev;
  });

  qc.setQueriesData('pipeline', prev => patchPipelineData(prev, id, patch));
}

function snapshotCandidateCaches(qc, id) {
  return {
    detail: qc.getQueryData(['candidate', id]),
    lists: qc.getQueriesData('candidates'),
    pipelines: qc.getQueriesData('pipeline'),
  };
}

function restoreCandidateCaches(qc, id, snapshot) {
  if (!snapshot) return;
  qc.setQueryData(['candidate', id], snapshot.detail);
  for (const [key, data] of snapshot.lists) qc.setQueryData(key, data);
  for (const [key, data] of snapshot.pipelines) qc.setQueryData(key, data);
}

// ── Queries ─────────────────────────────────────────────────────────

export function useCandidates(params = {}) {
  // keepPreviousData: without it every debounced keystroke is a cold query key,
  // isLoading flips true, and the page unmounts its own search box mid-typing.
  return useQuery(['candidates', params], () =>
    api.get('/candidates', { params }).then(r => r.data),
    { keepPreviousData: true }
  );
}

export function usePipeline(params = {}) {
  return useQuery(['pipeline', params], () =>
    api.get('/candidates/pipeline', { params }).then(r => r.data),
    { keepPreviousData: true }
  );
}

/**
 * Seeds from the list cache so clicking a row you were just looking at paints
 * immediately. Seeded data is marked `partial: true` and is stale on arrival,
 * so the full record (notes, salary, assessments) refetches in the background.
 */
export function useCandidate(id) {
  const qc = useQueryClient();
  return useQuery(['candidate', id], () =>
    api.get(`/candidates/${id}`).then(r => r.data),
    {
      enabled: !!id,
      initialData: () => {
        const cached = findCachedCandidate(qc, id);
        if (!cached) return undefined;
        return { candidate: cached, assessments: [], stageHistory: [], partial: true };
      },
      initialDataUpdatedAt: 0,
    }
  );
}

export function useTrackApplication(code, email) {
  // Public, shared/kiosk page: never keep the lookup (name, position, stage,
  // scores) in cache after the component goes away.
  return useQuery(
    ['track', code, email],
    () => trackApplication(code, email),
    {
      enabled: !!code && !!email,
      retry: false,
      cacheTime: 0,
      staleTime: 0,
      // The page renders the lookup failure inline; skip the global toast.
      meta: { silentError: true },
    }
  );
}

/** POST so the email stops landing in browser history and proxy logs. */
async function trackApplication(code, email) {
  const path = `/candidates/track/${encodeURIComponent(code)}`;
  try {
    const { data } = await publicApi.post(path, { email });
    return data;
  } catch (err) {
    // Fall back to the deprecated GET only when the POST route itself is
    // missing (Express answers an unknown route with HTML, never `{ error }`).
    const res = err.response;
    const routeMissing = (res?.status === 404 || res?.status === 405) && !res?.data?.error;
    if (!routeMissing) throw err;
    const { data } = await publicApi.get(path, { params: { email } });
    return data;
  }
}

// ── Mutations ───────────────────────────────────────────────────────

/**
 * Accepts FormData directly, or `{ data, onUploadProgress }` to render upload
 * progress. Uses UPLOAD_TIMEOUT: the 15s default aborted a large CV mid-flight
 * while the server still created the candidate, so the retry hit a 409.
 */
export function useCreateCandidate() {
  const qc = useQueryClient();
  return useMutation(
    variables => {
      const wrapped = variables && !(variables instanceof FormData) && 'data' in variables;
      const body = wrapped ? variables.data : variables;
      return api.post('/candidates', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: (wrapped && variables.timeout) || UPLOAD_TIMEOUT,
        onUploadProgress: wrapped ? variables.onUploadProgress : undefined,
      }).then(r => r.data);
    },
    { onSuccess: () => qc.invalidateQueries('candidates') }
  );
}

/**
 * Single-field writes (drag-and-drop stage moves, star ratings) patch the
 * caches instead of invalidating them — invalidating `pipeline` refetched an
 * unbounded endpoint after every drop, and the rating path refetched two lists
 * to change one number.
 */
export function useUpdateCandidate() {
  const qc = useQueryClient();
  return useMutation(
    ({ id, ...data }) => api.patch(`/candidates/${id}`, data).then(r => r.data),
    {
      onMutate: async ({ id, ...data }) => {
        if (!id) return undefined;
        await qc.cancelQueries(['candidate', id]);
        const snapshot = snapshotCandidateCaches(qc, id);
        patchCandidateCaches(qc, id, optimisticPatch(data));
        return snapshot;
      },
      onError: (_err, { id }, snapshot) => restoreCandidateCaches(qc, id, snapshot),
      onSuccess: (_res, { id, ...data }) => {
        // A stage change also writes stage history, sends mail and can
        // auto-advance, so refetch that one record — but nothing else.
        if (id && 'stage' in data) qc.invalidateQueries(['candidate', id]);
      },
    }
  );
}

export function useDeleteCandidate() {
  const qc = useQueryClient();
  return useMutation(
    id => api.delete(`/candidates/${id}`).then(r => r.data),
    { onSuccess: () => {
      qc.invalidateQueries('candidates');
      qc.invalidateQueries('pipeline');
    }}
  );
}

export function useSubmitAssessment() {
  const qc = useQueryClient();
  return useMutation(
    ({ id, ...data }) => api.post(`/candidates/assessments/${id}`, data).then(r => r.data),
    { onSuccess: (_, { id }) => {
      qc.invalidateQueries(['candidate', id]);
      qc.invalidateQueries('pipeline');
      qc.invalidateQueries('candidates');
    }}
  );
}

export function useRegenerateInsights(id) {
  const qc = useQueryClient();
  return useMutation(
    () => api.post(`/candidates/${id}/insights/regenerate`).then(r => r.data),
    { onSuccess: () => {
      qc.invalidateQueries(['candidate', id]);
      qc.invalidateQueries('candidates');
      qc.invalidateQueries('pipeline');
    }}
  );
}

export function useSimulateAssessments(id) {
  const qc = useQueryClient();
  return useMutation(
    () => api.post(`/candidates/assessments/${id}/simulate`).then(r => r.data),
    { onSuccess: () => {
      qc.invalidateQueries(['candidate', id]);
      qc.invalidateQueries('pipeline');
      qc.invalidateQueries('candidates');
    }}
  );
}
